// src/lib/conversationBus.ts

export type ConversationEventMap = {
  'image-updated': { conversationId: number; imageUrl: string };
  'title-updated': { conversationId: number; title: string };
  'description-updated': { conversationId: number; description: string };
  'field-updated': {
    conversationId: number;
    coreTaskId?: number;
    fieldName: string;
    value: any;
    layerId: number;
  };
  'conversation-updated': { conversationId: number; updates: Record<string, any>; newConversationData?: any };
  'conversation-created': { conversation: any };
  'layer-updated': {
    conversationId: number;
    layerId: number;
    layerData: Record<string, any>;
    sourceId: string; // 'cf-*' from CustomFields, 'multi-edit' from multi-edit, etc.
    skipInfoWindowReload?: boolean;
    coreTaskId?: number; // Phase's CoreTaskId (for auto-switching phases)
    conversationPhaseId?: number; // Phase's ConversationPhaseId (for auto-switching)
  };
  'layers-updated': {
    conversationId: number;
    layers: Array<{
      ConversationId: number;
      LayerId: number;
      LayerName: string;
      LayerColor: string;
      IsDefault: boolean;
      Color?: string;
    }>;
  };
  'reply-added': { conversationId: number; reply: any };
  'reply-updated': { conversationId: number; replyId: number; reply: any };
  'redflag-updated': { conversationId: number; isRedFlag: boolean };
  'important-updated': { conversationId: number; isImportant: boolean | null };
  'completed-updated': { conversationId: number; isCompleted: boolean };
  'conversation-deleted': { conversationId: number };
  'conversation-moved-to-column': {
    conversationId: number;
    fromGroupId: number;
    toGroupId: number;
    conversation: any;
  };
  'conversation-added-to-column': { conversation: any; toGroupId: number };
  'conversation-duplicated-from-infowindow': {
    conversation: any;
    dashboardGroupId: number;
    socketConnectionId: string;
  };
  'dashboard-refresh': { conversationId: number };
  'dashboard-refresh-body': { conversationId: number };
  'dashboard-groups-loading': { isLoading: boolean };
  'dashboard-created': { dashboardId: number; configValueId?: number; projectId?: number | null };
};

class ConversationBus
{
  private listeners = new Map<keyof ConversationEventMap, Set<Function>>();

  emit<K extends keyof ConversationEventMap>(event: K, data: ConversationEventMap[K]): void
  {
    const eventListeners = this.listeners.get(event);
    if (eventListeners)
    {
      eventListeners.forEach(listener =>
      {
        try
        {
          listener(data);
        }
        catch (error)
        {
          console.error(`Error in ConversationBus listener for event ${event}:`, error);
        }
      });
    }
  }

  on<K extends keyof ConversationEventMap>(
    event: K,
    callback: (data: ConversationEventMap[K]) => void,
  ): () => void
  {
    if (!this.listeners.has(event))
    {
      this.listeners.set(event, new Set());
    }

    const eventListeners = this.listeners.get(event)!;
    eventListeners.add(callback);

    return () =>
    {
      eventListeners.delete(callback);
      if (eventListeners.size === 0)
      {
        this.listeners.delete(event);
      }
    };
  }
}

export const conversationBus = new ConversationBus();
